import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { API, axios } from '../../utils/api';
import { L } from '../../utils/leafletIcons';
import { COLLABORAZIONI, DICHIARAZIONI, filtraAgenzie, inizialeAgenzia, testoMancano } from '../../utils/scheda';
import Icona from '../../components/cliente/Icone';
import { CreateAgencyDialog } from '../../components/features/CreateAgencyDialog';
import AgenzieDaSistemare from '../../components/staff/AgenzieDaSistemare';
import '../../components/staff/scheda.css';

// /admin/agenzie: bozza design/admin-agenzie-v3.html. "N agenzie, M hanno dichiarato"; quattro numeri (dichiarate,
// in attesa, non dichiarano, incomplete con "completa in automatico"); filtri su due righe e mappa per comune;
// righe con il logo grande, referente, dichiarazione e collaborazione, "Con noi", "Risponde in"; stato del
// completamento automatico (Parte 4) e scelta tra le candidate. Dati: backend/scheda_agenzie.py.

const PORTALI = { immobiliare: 'immobiliare.it', idealista: 'idealista', casa: 'casa.it' };
const STATI_COMPL = { in_coda: "in coda all'estrattore", completata: 'completata', da_confermare: 'da confermare', non_trovata: 'non trovata sui portali' };

// Il logo (da R2, lo salva il completamento automatico o la modifica dell'agenzia); se manca o non si carica, l'iniziale
export const Logo = ({ a, piccolo = false }) => {
  const [rotto, setRotto] = useState(false);
  return (
    <div className={`lg${piccolo ? ' piccolo' : ''}`} aria-hidden="true">
      {a.logo_url && !rotto ? <img src={a.logo_url} alt="" loading="lazy" onError={() => setRotto(true)} /> : inizialeAgenzia(a.nome)}
    </div>
  );
};

// Sotto il nome: marchio (se non è già nel nome) e comune
const marchioEComune = (a) => [a.marchio && !(a.nome || '').toLowerCase().includes(a.marchio.toLowerCase()) ? a.marchio : null, a.comune].filter(Boolean).join(' · ');

const Candidate = ({ a, onFatto }) => {
  const c = a.completamento || {};
  const scegli = async (corpo) => {
    try {
      const r = await axios.post(`${API}/agenzie/${a.id}/completamento/scegli`, corpo);
      toast.success(r.data.stato === 'completata' ? `Completata: ${(r.data.campi || []).join(', ') || 'niente da aggiungere'}` : 'Segnata come non trovata');
      onFatto();
    } catch (e) { toast.error(e.response?.data?.detail || 'Non riuscito'); }
  };
  return (
    <div className="candidate" onClick={(e) => e.stopPropagation()} role="presentation" data-testid={`candidate-${a.id}`}>
      <span style={{ color: 'var(--warn)' }}>Quale di queste è l'agenzia? ({PORTALI[c.portale] || c.portale})</span>
      {(c.candidati || []).map((cand, i) => (
        <div key={`${cand.id_portale || cand.nome}-${i}`}>
          <b title={cand.nome}>{cand.nome}</b><span className="muto">{[cand.indirizzo, cand.comune, cand.telefono].filter(Boolean).join(' · ')}</span>
          {cand.url && <a className="link" href={cand.url} target="_blank" rel="noopener noreferrer">pagina</a>}
          <button type="button" className="btn xs p" style={{ marginLeft: 'auto' }} onClick={() => scegli({ indice: i })}>È questa</button>
        </div>
      ))}
      <div><button type="button" className="btn xs" onClick={() => scegli({ nessuna: true })}>Nessuna di queste</button></div>
    </div>
  );
};

const Riga = ({ a, onFatto }) => {
  const navigate = useNavigate();
  const d = DICHIARAZIONI[a.dichiarazione] || DICHIARAZIONI.in_attesa_dichiarazione;
  const col = COLLABORAZIONI[a.collaborazione] || COLLABORAZIONI.in_attesa;
  const cn = a.con_noi || {};
  const compl = a.completamento?.stato;
  const sotto = marchioEComune(a);
  return (
    <div className="tr ag" onClick={() => navigate(`/admin/agenzie/${a.id}`)} role="link" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/agenzie/${a.id}`); }} data-testid={`agenzia-${a.id}`}>
      {/* `title`: il nome intero al passaggio del mouse, perché a video si accorcia con i puntini (scheda.css) */}
      <div className="nm"><Logo a={a} /><div style={{ minWidth: 0 }}><b title={a.nome}>{a.nome}</b>
        <span className="sotto">{sotto || (a.mancano?.includes('comune') ? null : '—')}</span>
        {a.mancano?.length > 0 && <span className="sotto mancano">{testoMancano(a.mancano)}{compl ? ` · ${STATI_COMPL[compl] || compl}` : ''}</span>}</div></div>
      <div className="ref">{a.referente?.nome || '—'}<span>{a.referente?.nota_breve || (a.referente ? '' : 'aggiungi referente')}</span></div>
      <div><span className={`stato-d ${d.classe}`}><i />{d.testo}</span><br /><span className={`dich ${col.classe}`}>{col.testo}</span></div>
      <div className="s">{cn.immobili} {cn.immobili === 1 ? 'immobile' : 'immobili'} · {cn.visite} {cn.visite === 1 ? 'visita' : 'visite'}{cn.proposte ? ` · ${cn.proposte} ${cn.proposte === 1 ? 'proposta' : 'proposte'}` : ''}
        <div className="bar"><i style={{ width: `${cn.resa || 0}%`, background: a.collaborazione === 'non_lavorabile' ? 'var(--bad)' : undefined }} /></div></div>
      <div className="s">{a.risponde?.testo || '—'}<span>{a.risponde?.sotto}</span></div>
      <Link className="btn sm" to={`/admin/agenzie/${a.id}`} onClick={(e) => e.stopPropagation()}>Apri</Link>
      {compl === 'da_confermare' && <Candidate a={a} onFatto={onFatto} />}
    </div>
  );
};

const Inquadra = ({ punti }) => {
  const mappa = useMap();
  useEffect(() => { if (punti.length > 1) mappa.fitBounds(punti, { padding: [40, 40], maxZoom: 12 }); else if (punti.length === 1) mappa.setView(punti[0], 12); }, [mappa, punti]);
  return null;
};

const CLASSE_PREVALENTE = { dichiarato: '', in_attesa_dichiarazione: 'w', non_dichiarato: 'no' };

const Mappa = ({ agenzie }) => {
  const [punti, setPunti] = useState(null);
  const [cercati, setCercati] = useState({});
  const [scelto, setScelto] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/agenzie/mappa`).then((r) => setPunti(r.data)).catch(() => setPunti([])); }, []);
  useEffect(() => {
    if (!punti) return undefined;
    let attivo = true;
    (async () => {
      for (const p of punti.filter((x) => x.lat == null)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const r = await axios.get(`${API}/geocode`, { params: { q: `${p.comune}, Italia` } });
          if (!attivo) return;
          if (r.data?.lat) setCercati((c) => ({ ...c, [p.comune]: [r.data.lat, r.data.lon] }));
        } catch { /* senza posizione */ }
      }
    })();
    return () => { attivo = false; };
  }, [punti]);
  const conPos = useMemo(() => (punti || []).map((p) => ({ ...p, pos: p.lat != null ? [p.lat, p.lon] : cercati[p.comune] })).filter((p) => p.pos), [punti, cercati]);
  const quanti = conPos.length;
  const posizioni = useMemo(() => conPos.map((p) => p.pos), [quanti]); // eslint-disable-line
  const sel = (punti || []).find((p) => p.comune === scelto) || conPos.slice().sort((a, b) => b.n - a.n)[0];
  const delComune = sel ? agenzie.filter((a) => sel.agenzie.includes(a.id)) : [];
  const conta = (k) => (sel?.conteggi || {})[k] || 0;
  return (
    <div className="mapv r" data-testid="mappa-agenzie">
      <div className="mp">
        <MapContainer center={[45.55, 9.2]} zoom={9} scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {conPos.map((p) => (
            <Marker key={p.comune} position={p.pos} title={`${p.comune}: ${p.n}`} eventHandlers={{ click: () => setScelto(p.comune) }}
              icon={L.divIcon({ className: '', iconSize: null, html: `<div class="pin-comune ${CLASSE_PREVALENTE[p.prevalente]}${sel?.comune === p.comune ? ' on' : ''}"><b>${p.n}</b><span>${p.comune.replace(/</g, '')}</span></div>` })} />
          ))}
          <Inquadra punti={posizioni} />
        </MapContainer>
      </div>
      <div className="side">
        {sel ? (
          <>
            <h3>{sel.comune} · {sel.n}</h3>
            <p>{conta('dichiarato')} dichiarate · {conta('in_attesa_dichiarazione')} in attesa · {conta('non_dichiarato')} no · toccate un altro punto per cambiare zona</p>
            <div className="rows">
              {delComune.map((a) => (
                <div key={a.id}><Logo a={a} piccolo /><div style={{ minWidth: 0 }}><b title={a.nome}>{a.nome}</b><span>{[a.referente?.nome, COLLABORAZIONI[a.collaborazione]?.testo.toLowerCase(), a.dichiarazione === 'dichiarato' ? 'dichiarato' : null].filter(Boolean).join(' · ')}</span></div>
                  <Link className="btn sm" to={`/admin/agenzie/${a.id}`}>Apri</Link></div>
              ))}
            </div>
          </>
        ) : <p className="vuoto">{punti ? 'Nessuna agenzia con un comune.' : 'Carico la mappa…'}</p>}
      </div>
      <div className="mleg"><span><i style={{ background: 'var(--ok)' }} />Dichiarate</span><span><i style={{ background: 'var(--warn)' }} />In attesa</span><span><i style={{ background: 'var(--bad)' }} />Non dichiarano</span><span>· il numero è quante agenzie in quel comune · colore = la maggioranza</span></div>
    </div>
  );
};

const Agenzie = () => {
  const [dati, setDati] = useState(null);
  const [f, setF] = useState({ dichiarazione: '', collaborazione: '', marchio: '', comune: '', altro: '', cerca: '' });
  const [mappa, setMappa] = useState(false);
  const [sistemare, setSistemare] = useState(false);
  const [tutte, setTutte] = useState(false);
  const [nuova, setNuova] = useState(false);
  const [versione, setVersione] = useState(0);
  const ricarica = () => setVersione((v) => v + 1);
  useEffect(() => { axios.get(`${API}/admin/agenzie/elenco`).then((r) => setDati(r.data)).catch(() => setDati(false)); }, [versione]);
  const visibili = useMemo(() => (dati ? filtraAgenzie(dati.agenzie, f) : []), [dati, f]);
  if (dati === false) return <main className="st wrap"><p className="vuoto">Non riesco a caricare le agenzie.</p></main>;
  if (!dati) return <main className="st wrap"><p className="vuoto">Carico le agenzie…</p></main>;
  const t = dati.totali;
  const imposta = (k, v) => setF((x) => ({ ...x, [k]: x[k] === v ? '' : v }));
  const completa = async (e) => {
    e.stopPropagation();
    const ids = dati.agenzie.filter((a) => (a.mancano || []).length).map((a) => a.id);
    try {
      const r = await axios.post(`${API}/agenzie/completa`, { ids });
      toast.success(`${r.data.in_coda} agenzie in coda: le completa l'estensione dell'ufficio, dopo gli annunci`);
      ricarica();
    } catch { toast.error('Non riuscito'); }
  };
  const mostrate = tutte ? visibili : visibili.slice(0, 40);
  return (
    <main className="st wrap" data-testid="agenzie">
      <div className="head r">
        <div><div className="k">Agenzie</div><h1>{t.tutte} agenzie, <em>{t.dichiarate} hanno dichiarato</em></h1>
          <p>La dichiarazione è ciò che conta: chi l'ha firmata, chi la deve ancora mandare, chi non la farà.</p></div>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          <Link className="btn" to="/admin/agenzie/doppioni">Possibili doppioni · {dati.doppioni}</Link>
          <button type="button" className="btn p" onClick={() => setNuova(true)}>+ Nuova agenzia</button>
        </div>
      </div>
      <div className="nums n4 r">
        <button type="button" className={`num g${f.dichiarazione === 'dichiarato' ? ' on' : ''}`} onClick={() => imposta('dichiarazione', 'dichiarato')}><b>{t.dichiarate}</b><span>hanno dichiarato</span></button>
        <button type="button" className={`num w${f.dichiarazione === 'in_attesa_dichiarazione' ? ' on' : ''}`} onClick={() => imposta('dichiarazione', 'in_attesa_dichiarazione')}><b>{t.in_attesa}</b><span>in attesa di dichiarazione</span></button>
        <button type="button" className={`num no${f.dichiarazione === 'non_dichiarato' ? ' on' : ''}`} onClick={() => imposta('dichiarazione', 'non_dichiarato')}><b>{t.non_dichiarano}</b><span>non dichiarano</span></button>
        <div className={`num${f.altro === 'incomplete' ? ' on' : ''}`} onClick={() => imposta('altro', 'incomplete')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') imposta('altro', 'incomplete'); }} data-testid="filtro-incomplete">
          <b>{t.incomplete}</b><span>incomplete · <button type="button" className="link" onClick={completa} data-testid="completa-automatico">completa in automatico</button></span>
        </div>
      </div>
      <div className="tools r">
        {[['', 'Tutte'], ['dichiarato', 'Dichiarate', 'var(--ok)'], ['in_attesa_dichiarazione', 'In attesa', 'var(--warn)'], ['non_dichiarato', 'Non dichiarano', 'var(--bad)']].map(([k, nome, colore]) => (
          <button type="button" key={k || 'tutte'} className={`chip${f.dichiarazione === k ? ' on' : ''}`} onClick={() => setF((x) => ({ ...x, dichiarazione: k }))}>{colore && <i style={{ background: colore }} />}{nome}</button>
        ))}
        <span className="sp" />
        <label className="srch"><Icona nome="cerca" /><input value={f.cerca} onChange={(e) => setF((x) => ({ ...x, cerca: e.target.value }))} placeholder="Cerca agenzia, comune, referente…" aria-label="Cerca agenzia" /></label>
      </div>
      <div className="tools r" style={{ gap: '.4rem' }}>
        <span className={`chip${f.collaborazione ? ' on' : ''}`}><select value={f.collaborazione} onChange={(e) => setF((x) => ({ ...x, collaborazione: e.target.value }))} aria-label="Collaborazione">
          <option value="">Collaborazione ▾</option>{Object.entries(COLLABORAZIONI).map(([k, v]) => <option key={k} value={k}>{v.testo}</option>)}</select></span>
        <span className={`chip${f.marchio ? ' on' : ''}`}><select value={f.marchio} onChange={(e) => setF((x) => ({ ...x, marchio: e.target.value }))} aria-label="Marchio">
          <option value="">Marchio ▾</option>{dati.marchi.map((m) => <option key={m} value={m}>{m}</option>)}</select></span>
        <span className={`chip${f.comune ? ' on' : ''}`}><select value={f.comune} onChange={(e) => setF((x) => ({ ...x, comune: e.target.value }))} aria-label="Comune">
          <option value="">Comune ▾</option>{dati.comuni.map((m) => <option key={m} value={m}>{m}</option>)}</select></span>
        <span className={`chip${f.altro ? ' on' : ''}`}><select value={f.altro} onChange={(e) => setF((x) => ({ ...x, altro: e.target.value }))} aria-label="Altro">
          <option value="">Altro ▾</option><option value="attivi">Con immobili attivi</option><option value="incomplete">Incomplete ({t.incomplete})</option></select></span>
        <span className="sp" />
        <button type="button" className={`chip${mappa ? ' on' : ''}`} onClick={() => setMappa(!mappa)}><Icona nome="mappa" />Mappa</button>
        <button type="button" className={`chip${sistemare ? ' on' : ''}`} onClick={() => setSistemare(!sistemare)}
          data-testid="chip-da-sistemare">Da sistemare</button>
      </div>
      {mappa && <Mappa agenzie={dati.agenzie} />}
      {/* Due elenchi che prima non esistevano: le agenzie che l'estrattore non riesce più a completare (si
          scrivono a mano, senza spendere pagine) e quelle spente senza un motivo scritto */}
      {sistemare && <AgenzieDaSistemare agenzie={dati.agenzie} onFatto={ricarica} />}
      <div className="tbl r">
        <div className="tr ag testa" style={{ cursor: 'default' }}><div>Agenzia</div><div>Referente</div><div>Dichiarazione · collaborazione</div><div>Con noi</div><div>Risponde in</div><div /></div>
        {!visibili.length && <div className="tr tutti"><span className="muto">Nessuna agenzia con questi filtri.</span></div>}
        {mostrate.map((a) => <Riga key={a.id} a={a} onFatto={ricarica} />)}
        {visibili.length > mostrate.length && <div className="tr tutti"><button type="button" onClick={() => setTutte(true)}>Mostra tutte le {visibili.length} →</button></div>}
      </div>
      {nuova && <CreateAgencyDialog open={nuova} onClose={() => setNuova(false)} onSuccess={() => { setNuova(false); ricarica(); }} />}
    </main>
  );
};

export default Agenzie;
